import React from 'react'
import styled from 'styled-components'

import Details from 'pages/shared/Alert/Details'
import AlertIcon from 'pages/shared/Alert/Icon'
import Tags from 'pages/shared/Alert/Tags'

import { AlertDescription } from 'types/Alert'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

import { alertCodeDisplayValue } from 'helpers/alerts'
import { longDateTime } from 'helpers/formatters'

type Props = {
  alertDescription: AlertDescription;
  adminComponent?: JSX.Element | null | undefined;
}

const SDA_DESCRIPTION_TEXT = 'Smart Diagnostics Alerts (SDA) are based on algorithms that run periodically in the cloud and are independent of the alerts triggered within the thermostat or HVAC equipment. SDAs use data and information from the equipment to trigger an alert when appropriate. Because of the way the algorithms run, many SDAs do not stay active like the equipment alerts.'

const AlertDescriptionView = ({ alertDescription, adminComponent }: Props) => {
  const isSda = alertDescription.code?.substring(0, 3) === 'NDA'

  // TODO: Why isn't this using pages/shared/Alert/...
  return (
    <S.AlertDescription>
      <S.Severity severity={alertDescription.severity + (isSda ? 'Sda' : '')} />
      <S.Header>
        <AlertIcon severity={alertDescription.severity + (isSda ? '-sda' : '')} size={40} />
        <div>
          <S.DescriptionArea>
            <S.Description>{alertDescription?.description}</S.Description>
            <Tags tags={alertDescription.tags} />
          </S.DescriptionArea>
          <S.Code>{alertCodeDisplayValue(alertDescription.code)}</S.Code>
          <S.LastUpdated>Last Updated: <span>{longDateTime(alertDescription.updatedAt)}</span></S.LastUpdated>

          {adminComponent}

          {isSda && <S.SdaDescription>{SDA_DESCRIPTION_TEXT}</S.SdaDescription>}
        </div>
      </S.Header>
      <Details
        problems={alertDescription.problems}
        possibleCauses={alertDescription.possibleCauses}
        additionalInfo={alertDescription.additionalInfo}
      />
    </S.AlertDescription>
  )
}

export default AlertDescriptionView

const S = {
  AlertDescription: styled.div`
    width: calc(100% - 10px);
    padding: 30px;

    @media ${screenSize.small} {
      padding: 0;
    }
  `,
  SdaDescription: styled.p`
    padding: 10px 20px 0 20px;
    margin-bottom: 0;
    font-size: 12px;
    color: ${color('secondaryText')};;
  `,
  Header: styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100%;
    padding-bottom: 30px;
  `,
  DescriptionArea: styled.div`
    display: flex;
    align-items: center;
    margin-top: -10px;
  `,
  Description: styled.div`
    color: ${color('headerText')};
    font-size: 25px;
    font-weight: 600;
    padding-left: 20px;
    line-height: normal;
  `,
  Code: styled.div`
    color: ${color('headerText')};
    font-size: 18px;
    line-height: normal;
    padding-left: 20px;
  `,
  LastUpdated: styled.span`
    font-size: 14px;
    color: ${color('secondaryText')};
    padding-left: 20px;
    padding-right: 20px;
    line-height: normal;
  `,
  Severity: styled.div.withConfig({
    shouldForwardProp: (prop) => !['severity'].includes(prop)
  })<{ severity: string | null }>`
    height: 10px;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    background:  ${({ severity }) => color(severity + 'Alert')};
  `
}
